import { getApiBase } from './apiBase.js';

const API_ROOT = `${getApiBase()}/studentplayground/api/agreements`;

export class AgreementApiError extends Error {
  constructor(message, status = 0, code = '', retryAllowed = false) {
    super(message);
    this.name = 'AgreementApiError';
    this.status = status;
    this.code = code;
    this.retryAllowed = retryAllowed === true;
  }
}

function authHeaders(extra = {}) {
  const token = localStorage.getItem('jwtToken');
  if (!token) throw new AgreementApiError('Authentication required.', 401, 'unauthenticated');
  return { Authorization: `Bearer ${token}`, ...extra };
}

async function parseError(response) {
  let data = {};
  try { data = await response.json(); } catch (_) { /* intentionally empty */ }
  const code = data.code || data.error_code || '';
  const message = typeof data.detail === 'string' ? data.detail : 'Agreement request failed.';
  return new AgreementApiError(message, response.status, code, data.retry_allowed);
}

async function request(path, options = {}) {
  const response = await fetch(`${API_ROOT}${path}`, {
    ...options,
    cache: 'no-store',
    credentials: 'same-origin',
    referrerPolicy: 'no-referrer',
    headers: authHeaders(options.headers || {})
  });
  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return null;
  return response.json();
}

export function isHiddenAgreementResponse(error) {
  return error instanceof AgreementApiError && [401, 403, 404].includes(error.status);
}

export async function getCurrentRequirements() {
  const data = await request('/requirements/current/');
  if (data && data.enabled === false) return [];
  const requirements = Array.isArray(data) ? data : (data?.requirements || data?.results || []);
  return [
    ...requirements.filter(item => item.status !== 'completed_stored'),
    ...requirements.filter(item => item.status === 'completed_stored')
  ];
}

export function getSigningProfile(signal) {
  return request('/signing-profile/', { signal });
}

export function initiateSigning(publicId, identity, idempotencyKey) {
  return request(`/requirements/${encodeURIComponent(publicId)}/signings/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify({ ...identity, idempotency_key: idempotencyKey })
  });
}

export function resumeSigning(publicId, launchMode = 'iframe') {
  return request(`/requirements/${encodeURIComponent(publicId)}/signings/resume/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ launch_mode: launchMode })
  });
}

export function restartLostSigning(publicId) {
  return request(`/requirements/${encodeURIComponent(publicId)}/signings/restart-local/`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}'
  });
}

export function restartFailedSigning(publicId, transactionId) {
  return request(`/requirements/${encodeURIComponent(publicId)}/signings/restart/`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirm_restart: true, transaction_id: transactionId })
  });
}

export function restartPendingSigning(publicId, transactionId) {
  return request(`/requirements/${encodeURIComponent(publicId)}/signings/restart-pending/`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirm_restart: true, transaction_id: transactionId })
  });
}
export const pendingRestartConfirmation = 'Ankstesnis pasirašymo bandymas bus sustabdytas. Galėsite iš naujo įvesti pasirašančio tėvo ar globėjo duomenis. Bandymo istorija bus išsaugota. Pradėti iš naujo?';
export const pendingRestartUnconfirmed = 'Ankstesnio bandymo sustabdymas nepatvirtintas. Naujas pasirašymas nepradėtas. Atnaujinkite būseną arba kreipkitės į administratorių.';

export async function prepareGatewayLaunch(launchUrl) {
  const response = await fetch(launchUrl, {
    method: 'POST',
    headers: authHeaders(),
    cache: 'no-store',
    credentials: 'same-origin',
    referrerPolicy: 'no-referrer'
  });
  if (!response.ok) throw await parseError(response);
  if (response.status !== 204) throw new AgreementApiError('Unexpected Gateway launch response.', response.status, 'invalid_launch_bootstrap');
}

export function reconcileRequirement(publicId, retryFinalization = false) {
  return request(`/requirements/${encodeURIComponent(publicId)}/reconcile/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ retry_finalization: retryFinalization === true })
  });
}

export async function openAgreementFile(publicId, kind = 'source') {
  const suffix = kind === 'artifact' ? 'artifact' : 'source';
  const response = await fetch(`${API_ROOT}/requirements/${encodeURIComponent(publicId)}/${suffix}/`, {
    headers: authHeaders(),
    cache: 'no-store',
    credentials: 'same-origin',
    referrerPolicy: 'no-referrer'
  });
  if (!response.ok) throw await parseError(response);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  // noopener returns null even when the tab opens; it cannot detect blocking.
  window.open(objectUrl, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
}

export function newIdempotencyKey() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
